// Contractor weekly reports (V0.4.15): one per week, read by SWI AI, compared with the week before and checked against
// PM C3. SWI staff only. Drop the PBS PDF to add a week.
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { api, write } from '../lib/api';
import { uploadFile } from '../lib/files';
import { fmtDate } from '../lib/format';
import { compareWeeks, norm, WEEK_STATUS, type WeeklyRead } from '../lib/weekly';
import { Button, Card, Empty, PageHeader, errText, useToast } from '../ui/kit';
import { C3Busy, C3PageLoading } from '../ui/C3Loader';

export type WeeklyRow = {
  id: string;
  site_id: string;
  week_no: number | null;
  period_from: string | null;
  period_to: string | null;
  report_date: string | null;
  title: string | null;
  file_id: string | null;
  read: WeeklyRead | null;
  status: string;
  error: string | null;
  decisions: Record<string, string>;
  pbs_asks: { text: string; due: string | null; sent_at?: string }[];
  internal_note: string | null;
  client_summary: string | null;
  client_sent_at: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
};
const NONE = '00000000-0000-0000-0000-000000000000';
export const weekName = (r: Pick<WeeklyRow, 'week_no' | 'title'>) => (r.week_no ? `Week ${r.week_no}` : r.title ?? 'Weekly report');
export const period = (r: Pick<WeeklyRow, 'period_from' | 'period_to'>) => (r.period_from && r.period_to ? `${fmtDate(r.period_from)} – ${fmtDate(r.period_to)}` : r.period_to ? fmtDate(r.period_to) : '—');
const byWeek = (a: WeeklyRow, b: WeeklyRow) => (b.period_to ?? b.created_at.slice(0, 10)).localeCompare(a.period_to ?? a.created_at.slice(0, 10)) || b.created_at.localeCompare(a.created_at);

/** Start a weekly report from a PDF: row → upload → SWI AI read. Returns the new report id. */
export async function addWeeklyReport(siteId: string, file: File): Promise<string> {
  const row = await write<{ id: string }>('weekly_reports', 'insert', { values: { site_id: siteId, title: file.name.replace(/\.pdf$/i, ''), status: 'received' }, summary: `Added weekly report ${file.name}` });
  const up = await uploadFile(file, { siteId, bucket: 'documents', linkedTable: 'weekly_reports', linkedId: row.id, title: file.name });
  await api('weekly-read', { report_id: row.id, file_id: (up.row as { id: string }).id });
  return row.id;
}

export default function Weekly() {
  const { siteId, can } = useApp();
  const toast = useToast();
  const nav = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [bld, setBld] = useState('');
  const list = useRows<WeeklyRow>(
    () => supabase.from('weekly_reports').select('*').eq('site_id', siteId ?? NONE).is('deleted_at', null).order('period_to', { ascending: false, nullsFirst: true }).order('created_at', { ascending: false }),
    [siteId],
    ['weekly_reports'],
  );
  const rows = useMemo(() => [...list.rows].sort(byWeek), [list.rows]);
  const read = rows.filter((r) => r.read);
  const latest = read[0] ?? null;
  const changes = useMemo(() => (latest ? compareWeeks(latest.read!, read[1]?.read ?? null, read.slice(2, 6).map((r) => r.read!)) : []), [latest, read]);
  const names = latest ? latest.read!.buildings.map((b) => b.name) : [];
  const editable = can('weekly_reports', 4);

  async function add(files: FileList | File[] | null) {
    const f = [...(files ?? [])].find((x) => /pdf/i.test(x.type) || /\.pdf$/i.test(x.name));
    if (!f || !siteId) return toast({ tone: 'error', text: 'Drop the weekly report as a PDF' });
    setBusy(true);
    try {
      const id = await addWeeklyReport(siteId, f);
      toast({ tone: 'ok', text: `${f.name} added — SWI AI is reading it (a minute or two)` });
      nav(`/weekly/${id}`);
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  // per building, last five reads: green = progress reported, orange = same words as the week before, grey = no works / not in the report
  const dots = (name: string) =>
    read.slice(0, 5).reverse().map((r, i, arr) => {
      const b = r.read!.buildings.find((x) => norm(x.name) === norm(name));
      const prev = i > 0 ? arr[i - 1]!.read!.buildings.find((x) => norm(x.name) === norm(name)) : null;
      if (!b || !b.progress || /no works?|nil\b/i.test(b.progress)) return 'none';
      if (prev?.progress && norm(prev.progress) === norm(b.progress)) return 'same';
      return 'ok';
    });
  const shown = bld ? rows.filter((r) => r.read?.buildings.some((b) => norm(b.name) === norm(bld))) : rows;

  return (
    <div
      className="wk-page"
      onDragOver={(e) => {
        if (!editable) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (editable) void add(e.dataTransfer.files);
      }}
    >
      <PageHeader
        title="Contractor weekly reports"
        sub="PBS weekly construction updates, one per week. SWI AI reads each report, compares it with the week before and checks it against PM C3."
        actions={
          editable ? (
            <Button kind="primary" disabled={busy} onClick={() => input.current?.click()}>
              {busy ? <C3Busy text="Uploading…" accent /> : "+ Add this week's report"}
            </Button>
          ) : null
        }
      />
      <input ref={input} type="file" accept="application/pdf,.pdf" hidden aria-label="Weekly report PDF" onChange={(e) => void add(e.target.files)} />
      {editable ? (
        <button type="button" className={`wk-drop ${over ? 'over' : ''}`} onClick={() => input.current?.click()} disabled={busy}>
          <b>Drop the PBS weekly PDF here</b>
          <span className="muted small">SWI AI reads the whole PDF in a minute or two — nothing is saved to the registers until a PM confirms.</span>
        </button>
      ) : null}

      {list.loading ? (
        <C3PageLoading label="Loading weekly reports" />
      ) : (
        <>
          <div className="wk-stats">
            <div className="wk-stat"><span className="k">Latest week</span><b>{latest ? weekName(latest) : '—'}</b><span className="muted small">{latest ? `${period(latest)} · received ${fmtDate(latest.created_at)}` : 'No reports yet'}</span></div>
            <div className="wk-stat"><span className="k">Changes vs week before</span><b>{changes.length}</b><span className="muted small">{['moved', 'new', 'repeated', 'dropped'].map((k) => `${changes.filter((c) => c.kind === k).length} ${k === 'moved' ? 'moved on' : k}`).join(' · ')}</span></div>
            <div className="wk-stat"><span className="k">Reports</span><b>{rows.length}</b><span className="muted small">{rows.filter((r) => r.status === 'reviewed').length} reviewed</span></div>
            <div className="wk-stat"><span className="k">SWI review</span><b>{latest ? WEEK_STATUS[latest.status]?.label ?? latest.status : '—'}</b><span className="muted small">{latest?.client_sent_at ? `Client summary sent ${fmtDate(latest.client_sent_at)}` : 'Client summary not sent'}</span></div>
          </div>

          <div className="wk-cols">
            <Card
              title="Weeks"
              pad={false}
              actions={
                names.length ? (
                  <div className="wk-chips" role="group" aria-label="Filter by building">
                    <button type="button" className={`chip ${bld ? '' : 'on'}`} onClick={() => setBld('')}>All buildings</button>
                    {names.map((n) => (
                      <button key={n} type="button" className={`chip ${bld === n ? 'on' : ''}`} onClick={() => setBld(n)}>{n}</button>
                    ))}
                  </div>
                ) : null
              }
            >
              {list.error ? <div className="error-box">{list.error}</div> : null}
              {!rows.length ? (
                <Empty>No weekly reports yet — drop this week's PBS PDF above.</Empty>
              ) : (
                <div className="wk-tblw">
                  <table className="wk-tbl">
                    <thead>
                      <tr><th>Week</th><th>Period</th><th>Received</th><th>SWI AI read</th><th>Status</th><th>Client</th></tr>
                    </thead>
                    <tbody>
                      {shown.map((r) => (
                        <tr key={r.id}>
                          <td><Link to={`/weekly/${r.id}`}><b>{weekName(r)}</b></Link>{r.title && r.week_no ? <div className="muted small">{r.title}</div> : null}</td>
                          <td>{period(r)}</td>
                          <td>{fmtDate(r.created_at)}</td>
                          <td>{r.read ? <span className="wk-badge ai">Read · {r.read.buildings.length} building{r.read.buildings.length === 1 ? '' : 's'}</span> : <span className={`wk-badge ${WEEK_STATUS[r.status]?.tone ?? 'mute'}`}>{WEEK_STATUS[r.status]?.label ?? r.status}</span>}</td>
                          <td><span className={`wk-badge ${WEEK_STATUS[r.status]?.tone ?? 'mute'}`}>{WEEK_STATUS[r.status]?.label ?? r.status}</span></td>
                          <td>{r.client_sent_at ? <span className="wk-badge ok">Sent {fmtDate(r.client_sent_at)}</span> : <span className="wk-badge mute">Not sent</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            {latest ? (
              <Card title="This week by building" pad={false}>
                <p className="muted small wk-pad">Last {Math.min(5, read.length)} weeks · orange = same words as the week before</p>
                {latest.read!.buildings.map((b) => (
                  <div key={b.name} className="wk-bline">
                    <div className="grow">
                      <b>{b.name}</b>
                      <div className="muted small wk-clip">{b.progress ?? 'No progress text'}</div>
                    </div>
                    <div className="wk-dots" aria-label={`${b.name}: progress over the last weeks`}>
                      {dots(b.name).map((d, i) => (
                        <span key={i} className={`wk-dot ${d}`} title={d === 'ok' ? 'Progress reported' : d === 'same' ? 'Same words as the week before' : 'No works / not in the report'} />
                      ))}
                    </div>
                  </div>
                ))}
              </Card>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
